import { useEffect, useRef } from "react";

import { notificationService, WebPushPermissionError } from "../api/services/notification.service";
import { SITE_ID } from "../assets/meta/meta";
import {
  buildWebPushSuppressionKey,
  getPermissionSuppression,
  getWebPushSuppression,
  setWebPushSuppression,
  shouldAutoSubscribeToWebPush,
} from "../common/webPushPermissionPolicy.mjs";
import { useAuthContext } from "./AuthContext";

export default function WebPushAutoPermission() {
  const { isUserLogin, userInfo, isLoading } = useAuthContext();
  const attemptedScopeRef = useRef("");
  const userId = userInfo?.id;

  useEffect(() => {
    const isSupported = notificationService.isWebPushSupported();
    const permission = isSupported ? window.Notification.permission : "denied";
    const suppression = getWebPushSuppression(
      window.localStorage,
      SITE_ID,
      userId,
    );

    if (!shouldAutoSubscribeToWebPush({
      isUserLogin,
      userId,
      siteId: SITE_ID,
      isLoading,
      isSupported,
      permission,
      suppression,
    })) {
      return;
    }

    const scope = buildWebPushSuppressionKey(SITE_ID, userId);
    if (!scope || attemptedScopeRef.current === scope) return;
    attemptedScopeRef.current = scope;

    let cancelled = false;
    const subscribe = async () => {
      try {
        await notificationService.subscribeToWebPush();
      } catch (error) {
        if (cancelled || !(error instanceof WebPushPermissionError)) return;
        const nextSuppression = getPermissionSuppression(error.permission);
        if (nextSuppression) {
          setWebPushSuppression(
            window.localStorage,
            SITE_ID,
            userId,
            nextSuppression,
          );
        }
      }
    };

    void subscribe();
    return () => {
      cancelled = true;
    };
  }, [isLoading, isUserLogin, userId]);

  return null;
}
